import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ============================================================================
// post-client-mod -- 搜索页面（公开）
//
// 路径：/search
// 公开搜索页面，支持关键词搜索、分类过滤、标签过滤。
// ============================================================================
import * as React from 'react';
import { useRoute } from '@hvsin/mod-route';
import { useSearchPosts } from '../hooks/use-search-posts.js';
import { useCategories } from '../hooks/use-categories.js';
import { useTags } from '../hooks/use-tags.js';
import { getMarkdownExcerpt } from '../util/markdown.js';
/**
 * 搜索页
 */
export function SearchPage() {
    const { Link, useLocation } = useRoute();
    const location = useLocation();
    // 从 URL 查询参数读取搜索关键词（避免 useSearchParams 引用变化导致循环）
    const urlQuery = React.useMemo(() => new URLSearchParams(location.search).get('q') || '', [location.search]);
    const [keyword, setKeyword] = React.useState(urlQuery);
    const [submittedKeyword, setSubmittedKeyword] = React.useState(urlQuery);
    const [selectedCategory, setSelectedCategory] = React.useState('');
    const [selectedTag, setSelectedTag] = React.useState('');
    const { posts, pagination, loading, error, setPage } = useSearchPosts({
        q: submittedKeyword,
        categorySlug: selectedCategory || undefined,
        tagSlug: selectedTag || undefined,
        pageSize: 10,
    });
    // URL 中 q 参数变化时同步触发搜索（处理从其他页面带参数跳转过来的场景）
    const prevUrlQueryRef = React.useRef(urlQuery);
    React.useEffect(() => {
        if (urlQuery !== prevUrlQueryRef.current && urlQuery !== submittedKeyword) {
            prevUrlQueryRef.current = urlQuery;
            setKeyword(urlQuery);
            setSubmittedKeyword(urlQuery);
            setPage(1);
        }
    }, [urlQuery, submittedKeyword]);
    const { categories } = useCategories();
    const { tags } = useTags();
    const handleSearch = (e) => {
        e.preventDefault();
        setSubmittedKeyword(keyword.trim());
        setPage(1);
    };
    const handleClearFilters = () => {
        setSelectedCategory('');
        setSelectedTag('');
        setKeyword('');
        setSubmittedKeyword('');
    };
    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleDateString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
    };
    return (_jsxs("div", { style: styles.page, children: [_jsx("header", { style: styles.header, children: _jsx("div", { style: styles.headerContent, children: _jsx("h1", { style: styles.title, children: "\u641C\u7D22\u6587\u7AE0" }) }) }), _jsxs("main", { style: styles.main, children: [_jsxs("form", { onSubmit: handleSearch, style: styles.searchForm, children: [_jsx("input", { type: "text", value: keyword, onChange: (e) => setKeyword(e.target.value), placeholder: "\u8F93\u5165\u5173\u952E\u8BCD\u641C\u7D22...", style: styles.searchInput }), _jsx("button", { type: "submit", style: styles.searchButton, children: "\u641C\u7D22" })] }), (categories.length > 0 || tags.length > 0) && (_jsxs("div", { style: styles.filters, children: [categories.length > 0 && (_jsxs("select", { value: selectedCategory, onChange: (e) => { setSelectedCategory(e.target.value); setPage(1); }, style: styles.filterSelect, children: [_jsx("option", { value: "", children: "\u6240\u6709\u5206\u7C7B" }), categories.map(cat => (_jsxs("option", { value: cat.slug, children: [cat.name, " (", cat.postCount, ")"] }, cat.slug)))] })), tags.length > 0 && (_jsxs("select", { value: selectedTag, onChange: (e) => { setSelectedTag(e.target.value); setPage(1); }, style: styles.filterSelect, children: [_jsx("option", { value: "", children: "\u6240\u6709\u6807\u7B7E" }), tags.map(tag => (_jsxs("option", { value: tag.slug, children: ["#", tag.name, " (", tag.postCount, ")"] }, tag.slug)))] })), (selectedCategory || selectedTag) && (_jsx("button", { type: "button", onClick: handleClearFilters, style: styles.clearButton, children: "\u6E05\u9664\u8FC7\u6EE4" }))] })), submittedKeyword && !loading && (_jsxs("div", { style: styles.resultInfo, children: ["\u5173\u952E\u8BCD\u300C", submittedKeyword, "\u300D", pagination ? `找到 ${pagination.total} 条结果` : '无搜索结果'] })), loading && _jsx("div", { style: styles.loading, children: "\u641C\u7D22\u4E2D..." }), error && _jsx("div", { style: styles.error, children: error }), !loading && !error && submittedKeyword && posts.length === 0 && (_jsxs("div", { style: styles.empty, children: [_jsx("p", { style: styles.emptyText, children: "\u6CA1\u6709\u627E\u5230\u76F8\u5173\u6587\u7AE0" }), _jsx("p", { style: styles.emptySubtext, children: "\u8BD5\u8BD5\u5176\u4ED6\u5173\u952E\u8BCD\u6216\u8C03\u6574\u8FC7\u6EE4\u5668" })] })), !loading && !submittedKeyword && (_jsx("div", { style: styles.empty, children: _jsx("p", { style: styles.emptyText, children: "\u8BF7\u8F93\u5165\u5173\u952E\u8BCD\u5F00\u59CB\u641C\u7D22" }) })), !loading && posts.length > 0 && (_jsx("div", { style: styles.results, children: posts.map(post => (_jsxs("article", { style: styles.resultItem, children: [_jsx("h2", { style: styles.resultTitle, children: _jsx(Link, { to: `/post/${post.id}`, style: styles.resultLink, children: post.title }) }), _jsxs("div", { style: styles.resultMeta, children: [_jsx("time", { style: styles.resultDate, children: formatDate(post.createdAt) }), post.categorySlug && (_jsx("span", { style: styles.categoryBadge, children: post.categorySlug })), post.tagSlugs?.map(slug => (_jsxs("span", { style: styles.tagBadge, children: ["#", slug] }, slug)))] }), _jsx("p", { style: styles.resultExcerpt, children: getMarkdownExcerpt(post.content, 150) })] }, post.id))) })), !loading && pagination && pagination.totalPages > 1 && (_jsxs("div", { style: styles.pagination, children: [_jsx("button", { onClick: () => setPage(Math.max(1, pagination.page - 1)), disabled: pagination.page <= 1, style: styles.pageButton, children: "\u2190 \u4E0A\u4E00\u9875" }), _jsxs("span", { style: styles.pageInfo, children: [pagination.page, " / ", pagination.totalPages] }), _jsx("button", { onClick: () => setPage(Math.min(pagination.totalPages, pagination.page + 1)), disabled: pagination.page >= pagination.totalPages, style: styles.pageButton, children: "\u4E0B\u4E00\u9875 \u2192" })] }))] })] }));
}
const styles = {
    page: {
        minHeight: '100vh',
        backgroundColor: '#fafafa',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        color: '#1a1a1a',
    },
    header: {
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #eaeaea',
        padding: '40px 24px 30px',
        textAlign: 'center',
    },
    headerContent: {
        maxWidth: '720px',
        margin: '0 auto',
    },
    title: {
        margin: 0,
        fontSize: '28px',
        fontWeight: 700,
        color: '#1a1a1a',
    },
    main: {
        maxWidth: '720px',
        margin: '0 auto',
        padding: '32px 24px 60px',
    },
    searchForm: {
        display: 'flex',
        gap: '8px',
        marginBottom: '20px',
    },
    searchInput: {
        flex: 1,
        padding: '12px 16px',
        border: '2px solid #e5e7eb',
        borderRadius: '8px',
        fontSize: '16px',
        outline: 'none',
        transition: 'border-color 0.2s ease',
    },
    searchButton: {
        padding: '12px 24px',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '15px',
        fontWeight: 600,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    filters: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginBottom: '20px',
    },
    filterSelect: {
        padding: '8px 12px',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        fontSize: '14px',
        backgroundColor: '#ffffff',
        cursor: 'pointer',
        outline: 'none',
    },
    clearButton: {
        padding: '8px 12px',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        backgroundColor: '#ffffff',
        fontSize: '13px',
        color: '#ef4444',
        cursor: 'pointer',
    },
    resultInfo: {
        fontSize: '14px',
        color: '#6b7280',
        marginBottom: '16px',
        padding: '8px 0',
        borderBottom: '1px solid #f3f4f6',
    },
    loading: {
        padding: '60px 0',
        textAlign: 'center',
        color: '#999',
        fontSize: '15px',
    },
    error: {
        padding: '16px',
        backgroundColor: '#fff5f5',
        border: '1px solid #fed7d7',
        borderRadius: '8px',
        color: '#c53030',
        fontSize: '14px',
    },
    empty: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        gap: '8px',
    },
    emptyText: {
        margin: 0,
        fontSize: '16px',
        color: '#6b7280',
        fontWeight: 500,
    },
    emptySubtext: {
        margin: 0,
        fontSize: '13px',
        color: '#9ca3af',
    },
    results: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    resultItem: {
        padding: '20px',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        transition: 'box-shadow 0.15s ease',
    },
    resultTitle: {
        margin: '0 0 8px 0',
        fontSize: '18px',
        fontWeight: 600,
        lineHeight: '1.4',
    },
    resultLink: {
        color: '#1a1a1a',
        textDecoration: 'none',
    },
    resultMeta: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
    },
    resultDate: {
        fontSize: '13px',
        color: '#9ca3af',
    },
    categoryBadge: {
        padding: '2px 8px',
        backgroundColor: '#f0fdf4',
        color: '#166534',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 500,
    },
    tagBadge: {
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '12px',
        color: '#6366f1',
        backgroundColor: '#eef2ff',
    },
    resultExcerpt: {
        margin: 0,
        fontSize: '14px',
        lineHeight: '1.6',
        color: '#6b7280',
    },
    pagination: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        marginTop: '32px',
        paddingTop: '24px',
        borderTop: '1px solid #eaeaea',
    },
    pageButton: {
        padding: '8px 16px',
        border: '1px solid #eaeaea',
        borderRadius: '6px',
        backgroundColor: '#ffffff',
        fontSize: '14px',
        color: '#555',
        cursor: 'pointer',
    },
    pageInfo: {
        fontSize: '14px',
        color: '#999',
    },
};
//# sourceMappingURL=search-page.js.map